import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";

// Heroicons components
const SparklesIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
  </svg>
);

const ArrowRightIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
  </svg>
);

const PlayIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
  </svg>
);

type Mode = "chat" | "voice";

interface Message {
    role: "User" | "AI";
    text: string;
    id: number;
}

const chatScript: Omit<Message, "id">[] = [
    { role: "User", text: "How do I integrate FloGPT with my website?" },
    { role: "AI", text: "It's simple! Paste our JavaScript snippet before </body>." },
    { role: "User", text: "Can it handle multiple languages?" },
    { role: "AI", text: "Yes — 50+ languages with automatic detection." },
    { role: "User", text: "Does it learn from our docs?" },
    { role: "AI", text: "Absolutely. Connect URLs, PDFs, and your CMS in minutes." },
];

const voiceScript: Omit<Message, "id">[] = [
    { role: "User", text: "Hi — I'd like to book a haircut for Saturday." },
    { role: "AI", text: "Of course. I have 11am, 2pm, or 4pm Saturday — which works?" },
    { role: "User", text: "2pm please." },
    { role: "AI", text: "Booked at 2pm with Maya. Confirmation SMS on its way." },
    { role: "User", text: "Perfect, thanks!" },
    { role: "AI", text: "Anytime. Have a great day!" },
];

export default function Hero() {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [chatIndex, setChatIndex] = useState(0);
    const [typedText, setTypedText] = useState("");
    const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [typingRole, setTypingRole] = useState<"User" | "AI" | null>(null);
    const [hasFinished, setHasFinished] = useState(false);
    const [mode, setMode] = useState<Mode>("chat");
    const ref = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: false });
    const rafIdRef = useRef<number | null>(null);
    const lastMouseRef = useRef<{ x: number; y: number } | null>(null);

    const navigate = useNavigate();

    // Optimized mouse tracking with throttling
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            lastMouseRef.current = {
                x: (e.clientX / window.innerWidth) * 2 - 1,
                y: (e.clientY / window.innerHeight) * 2 - 1,
            };
            if (rafIdRef.current === null) {
                rafIdRef.current = window.requestAnimationFrame(() => {
                    rafIdRef.current = null;
                    if (lastMouseRef.current) {
                        setMousePosition(lastMouseRef.current);
                    }
                });
            }
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, []);

    // Simplified animation variants
    const textVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: (i: number) => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" }
        }),
        hover: { scale: 1.05, transition: { duration: 0.3 } }
    };

    const letterVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: (i: number) => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.02, duration: 0.4, ease: "easeOut" }
        })
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 12 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { delayChildren: 0.1, staggerChildren: 0.08 }
        }
    };

    const fadeUp = {
        hidden: { opacity: 0, y: 18 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };

    const statsVariants = {
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };

    const messageVariants = {
        hidden: { opacity: 0, y: 8 },
        visible: (i: number) => ({
            opacity: 1,
            y: 0,
            transition: { delay: 0.08 + i * 0.04, duration: 0.22, ease: "easeOut" }
        })
    };

    const headlineWords = ["Transform", "Your", "Business", "with"];

    const switchMode = (next: Mode) => {
        if (next === mode) return;
        setMode(next);
        setChatIndex(0);
        setDisplayedMessages([]);
        setHasFinished(false);
        setIsTyping(false);
        setTypingRole(null);
        setTypedText("");
    };

    // Typing simulation — runs the active script for the current mode, then flips
    // to the other mode so the demo keeps cycling between chat and voice.
    useEffect(() => {
        if (hasFinished) return;
        const activeScript = mode === "chat" ? chatScript : voiceScript;
        if (chatIndex >= activeScript.length) {
            setTimeout(() => {
                setDisplayedMessages([]);
                setChatIndex(0);
                setMode((prev) => (prev === "chat" ? "voice" : "chat"));
                setHasFinished(false);
            }, 2500);
            setHasFinished(true);
            return;
        }

        const current = activeScript[chatIndex];
        setTypingRole(current.role);
        setTypedText("");

        const preDelay = 800;
        const baseDelay = 40;
        let timeoutId: number | undefined;
        let intervalId: number | undefined;
        let postTimeoutId: number | undefined;

        timeoutId = window.setTimeout(() => {
            setIsTyping(true);
            let i = 0;
            intervalId = window.setInterval(() => {
                i += 1;
                setTypedText(current.text.slice(0, i));
                if (i >= current.text.length) {
                    if (intervalId !== undefined) {
                        window.clearInterval(intervalId);
                    }
                    // Add message with unique ID to prevent duplicates
                    setDisplayedMessages(prev => {
                        const newMessage: Message = { 
                            ...current, 
                            id: chatIndex 
                        };
                        // Check if message already exists
                        if (prev.some(msg => msg.id === chatIndex)) {
                            return prev;
                        }
                        return [...prev, newMessage].slice(-6);
                    });
                    setIsTyping(false);
                    setTypingRole(null);
                    setTypedText("");
                    postTimeoutId = window.setTimeout(() => {
                        setChatIndex(prev => prev + 1);
                    }, 1000);
                }
            }, baseDelay);
        }, preDelay);

        return () => {
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
            if (intervalId !== undefined) window.clearInterval(intervalId);
            if (postTimeoutId !== undefined) window.clearTimeout(postTimeoutId);
        };
    }, [chatIndex, hasFinished, mode]);

    // Debounced auto-scroll
    useEffect(() => {
        if (!chatContainerRef.current) return;
        const timer = setTimeout(() => {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
        }, 50);
        return () => clearTimeout(timer);
    }, [displayedMessages, typedText, isTyping]);

    return (
        <motion.div 
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="relative isolate px-6 pt-14 lg:px-8 min-h-screen flex items-center overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #1e293b 100%)" }}
        >
            {/* Simplified Background - Only 3 key elements */}
            <div className="absolute inset-0 -z-10">
                {/* Primary gradient blob */}
                <motion.div
                    animate={{
                        opacity: [0.2, 0.35, 0.2],
                        scale: [1, 1.15, 1],
                        x: mousePosition.x * 20,
                        y: mousePosition.y * 20,
                    }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute left-[calc(50%-11rem)] top-[-10rem] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-blue-500 via-purple-500 to-indigo-600 opacity-15 blur-3xl sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
                />

                {/* Secondary gradient */}
                <motion.div
                    animate={{
                        opacity: [0.15, 0.25, 0.15],
                        scale: [1.1, 1, 1.1],
                        x: mousePosition.x * -15,
                        y: mousePosition.y * -15,
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute right-[calc(50%-15rem)] bottom-[-15rem] aspect-[1155/678] w-[30rem] -translate-x-1/2 bg-gradient-to-bl from-indigo-600 via-pink-500 to-blue-500 opacity-14 blur-3xl sm:right-[calc(50%-25rem)] sm:w-[60rem]"
                />

                {/* Removed floating geometric shapes for cleaner background */}

                {/* Enhanced animated grid pattern */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-80"
                    style={{
                        backgroundImage: `
                            radial-gradient(rgba(255,255,255,0.2) 1px, transparent 1px),
                            radial-gradient(rgba(255,255,255,0.15) 2px, transparent 2px),
                            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                            radial-gradient(rgba(59,130,246,0.15) 1px, transparent 1px),
                            radial-gradient(rgba(249,115,22,0.12) 1px, transparent 1px),
                            radial-gradient(rgba(236,72,153,0.1) 1px, transparent 1px)
                        `,
                        backgroundSize: "30px 30px, 40px 40px, 40px 40px, 40px 40px, 60px 60px, 80px 80px, 100px 100px",
                        willChange: "background-position, opacity",
                        transform: "translateZ(0)"
                    }}
                    animate={{ 
                        backgroundPosition: [
                            "0px 0px, 0px 0px, 0px 0px, 0px 0px, 0px 0px, 0px 0px, 0px 0px",
                            "30px 30px, 40px 40px, 20px 0px, 0px 20px, 30px 30px, 40px 40px, 50px 50px",
                            "0px 0px, 0px 0px, 0px 0px, 0px 0px, 0px 0px, 0px 0px, 0px 0px"
                        ],
                        opacity: [0.7, 1, 0.7]
                    }}
                    transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
                />
                
                {/* Secondary grid layer with diagonal patterns */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-60"
                    style={{
                        backgroundImage: `
                            radial-gradient(rgba(236,72,153,0.15) 1px, transparent 1px),
                            linear-gradient(45deg, rgba(255,255,255,0.08) 1px, transparent 1px),
                            linear-gradient(-45deg, rgba(255,255,255,0.08) 1px, transparent 1px),
                            radial-gradient(rgba(16,185,129,0.1) 1px, transparent 1px)
                        `,
                        backgroundSize: "50px 50px, 50px 50px, 50px 50px, 70px 70px",
                        willChange: "background-position, opacity",
                        transform: "translateZ(0)"
                    }}
                    animate={{ 
                        backgroundPosition: [
                            "0px 0px, 0px 0px, 0px 0px, 0px 0px",
                            "25px 25px, 25px 0px, 0px 25px, 35px 35px",
                            "0px 0px, 0px 0px, 0px 0px, 0px 0px"
                        ],
                        opacity: [0.5, 0.8, 0.5]
                    }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                />
                
                {/* Tertiary grid layer with hexagonal pattern */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-45"
                    style={{
                        backgroundImage: `
                            radial-gradient(rgba(168,85,247,0.12) 1px, transparent 1px),
                            radial-gradient(rgba(59,130,246,0.08) 1px, transparent 1px),
                            linear-gradient(30deg, rgba(255,255,255,0.05) 1px, transparent 1px),
                            linear-gradient(-30deg, rgba(255,255,255,0.05) 1px, transparent 1px)
                        `,
                        backgroundSize: "40px 40px, 40px 40px, 40px 40px",
                    }}
                />

                {/* Pulsing rings - reduced to 2 */}
                {[0, 1].map((i) => (
                    <motion.div
                        key={`ring-${i}`}
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border border-white/10 rounded-full"
                        style={{ width: `${400 + i * 250}px`, height: `${400 + i * 250}px` }}
                        animate={{
                            scale: [1, 1.08, 1],
                            opacity: [0.1, 0.25, 0.1],
                        }}
                        transition={{
                            duration: 8 + i * 2,
                            repeat: Infinity,
                            delay: i * 0.8,
                            ease: "easeInOut"
                        }}
                    />
                ))}
            </div>

            <div className="mx-auto w-full max-w-[1200px] xl:max-w-[1400px] px-4 sm:px-6 lg:px-8 py-20 sm:py-32 lg:py-20">
                {/* Badge */}
                <motion.div variants={fadeUp} className="flex justify-center mb-12 lg:mb-10">
                    <div className="relative rounded-full px-6 py-3 text-sm leading-6 text-white ring-1 ring-white/20 bg-white/10 backdrop-blur-sm">
                        <SparklesIcon className="inline w-6 h-6 mr-2" /> Powered by Advanced AI
                    </div>
                </motion.div>

                {/* Hero content */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    {/* Left: Headline and CTA */}
                    <motion.div variants={containerVariants} className="lg:col-span-6 text-center lg:text-left">
                        {/*
                          The visible H1 splits each letter into its own motion.span
                          for the entrance animation. Some crawlers and AI engines
                          stitch the per-letter spans incorrectly when extracting a
                          page title, so the accessible name and an `sr-only` sibling
                          provide the canonical, single-string headline.
                        */}
                        <motion.h1
                            aria-label="Transform Your Business with AI Chat and Voice Agents"
                            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-white leading-[1.1]"
                        >
                            <span className="sr-only">Transform Your Business with AI Chat and Voice Agents</span>
                            <span aria-hidden="true">
                                <span className="flex flex-wrap justify-center lg:justify-start gap-x-3 lg:gap-x-4 gap-y-2 mb-3 lg:mb-4">
                                    {headlineWords.map((word, i) => (
                                        <motion.span
                                            key={word + i}
                                            custom={i}
                                            variants={textVariants}
                                            whileHover="hover"
                                            className="inline-block origin-bottom cursor-pointer"
                                        >
                                            {word.split('').map((letter, letterIndex) => (
                                                <motion.span
                                                    key={letterIndex}
                                                    custom={i * 5 + letterIndex}
                                                    variants={letterVariants}
                                                    className="inline-block"
                                                    whileHover={{
                                                        scale: 1.1,
                                                        color: "#f97316",
                                                        transition: { duration: 0.2 }
                                                    }}
                                                >
                                                    {letter}
                                                </motion.span>
                                            ))}
                                        </motion.span>
                                    ))}
                                </span>
                                <motion.span
                                    variants={fadeUp}
                                    className="inline-block relative"
                                    whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                                >
                                    <motion.span
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        style={{
                                            background: "linear-gradient(90deg, #f97316, #ec4899, #3b82f6, #f97316)",
                                            backgroundSize: "200% 100%",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text"
                                        }}
                                    >
                                        AI Chat & Voice Agents
                                    </motion.span>
                                </motion.span>
                            </span>
                        </motion.h1>
                        <motion.p variants={fadeUp} className="mt-6 text-lg lg:text-xl leading-relaxed text-white/90 max-w-2xl lg:max-w-none">
                            Deploy AI agents that <span className="font-semibold text-white">chat on your website</span> and <span className="font-semibold text-white">answer phone calls</span> — grounded in your own knowledge base. Every conversation rolls into a <span className="font-semibold text-white">unified CRM inbox</span> with identity resolution and AI-to-human handover. <span className="text-orange-500 font-semibold">No coding required.</span>
                        </motion.p>
                        <motion.div variants={fadeUp} className="mt-8 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 sm:gap-6">
                            <button className="group" onClick={() => navigate("/contact")}>
                                <div className="relative inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-pink-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/80 to-pink-600/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <span className="relative z-10 flex items-center">
                                        Start Building 
                                        <ArrowRightIcon className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </div>
                            </button>
                        </motion.div>
                    </motion.div>

                    {/* Right: Visual mock */}
                    <motion.div variants={containerVariants} className="lg:col-span-6 relative">
                        {/* Simplified background glow */}
                        <motion.div
                            className="absolute -z-10 inset-0 w-full h-full rounded-3xl blur-3xl"
                            style={{
                                background: "radial-gradient(ellipse at center, rgba(59,130,246,0.2) 0%, rgba(236,72,153,0.15) 40%, transparent 70%)"
                            }}
                            animate={{ 
                                scale: [1, 1.05, 1], 
                                opacity: [0.4, 0.6, 0.4],
                            }}
                            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                        />
                        
                        <motion.div 
                            variants={cardVariants} 
                            whileHover={{ y: -6, scale: 1.01 }} 
                            transition={{ type: "spring", stiffness: 200, damping: 20 }} 
                            className="relative rounded-3xl border border-white/15 bg-white/5 backdrop-blur-md p-6 sm:p-8 shadow-2xl"
                        >
                            <div className="flex items-center justify-between gap-2 text-xs text-white/80 mb-4">
                                <div className="flex items-center gap-2">
                                    <motion.div
                                        className="h-2 w-2 rounded-full bg-emerald-400"
                                        animate={{ opacity: [0.4, 1, 0.4] }}
                                        transition={{ duration: 1.4, repeat: Infinity }}
                                    />
                                    {mode === "chat" ? "Live web chat" : "Live phone call · 00:42"}
                                </div>
                                <div className="flex rounded-full border border-white/15 bg-white/5 p-1">
                                    {(["chat", "voice"] as Mode[]).map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => switchMode(m)}
                                            className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                                                mode === m
                                                    ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow"
                                                    : "text-white/70 hover:text-white"
                                            }`}
                                        >
                                            {m === "chat" ? "Chat" : "Voice"}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div className="flex gap-3 items-center">
                                    <div className="h-10 w-10 rounded-xl flex items-center justify-center overflow-hidden">
                                        <img
                                            src="/images/flogpt-iso.png"
                                            alt="FloGPT"
                                            width={48}
                                            height={48}
                                            decoding="async"
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold text-lg">
                                            {mode === "chat" ? "FloGPT Chat Widget" : "FloGPT Voice Agent"}
                                        </div>
                                        <div className="text-white/70">
                                            {mode === "chat" ? "Brandable, multilingual web chat" : "Inbound + outbound phone calls 24/7"}
                                        </div>
                                    </div>
                                </div>
                                <div ref={chatContainerRef} className="relative flex flex-col gap-3 max-h-64 sm:max-h-72 overflow-y-auto pr-2 scroll-smooth w-full">
                                    {/* Only show displayedMessages, typing indicator shown separately */}
                                    {displayedMessages.map((m, i) => (
                                        <motion.div 
                                            key={`chat-${m.id}`}
                                            custom={i} 
                                            variants={messageVariants} 
                                            initial="hidden" 
                                            animate="visible" 
                                            className={`w-fit max-w-[90%] sm:max-w-[75%] break-words whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm shadow ${
                                                m.role === 'AI' 
                                                    ? 'bg-white/10 text-white self-end' 
                                                    : 'bg-gradient-to-r from-orange-500 to-pink-500 text-white self-start'
                                            }`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <span className="font-semibold text-xs opacity-70">
                                                    {m.role === "AI" ? "AI" : mode === "chat" ? "User" : "Caller"}
                                                </span>
                                            </div>
                                            <div className="mt-1">{m.text}</div>
                                        </motion.div>
                                    ))}
                                    {/* Show typing indicator separately */}
                                    {isTyping && typingRole && (
                                        <motion.div 
                                            key="typing-indicator"
                                            variants={messageVariants} 
                                            initial="hidden" 
                                            animate="visible" 
                                            className={`w-fit max-w-[90%] sm:max-w-[75%] break-words whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm shadow ${
                                                typingRole === 'AI' 
                                                    ? 'bg-white/10 text-white self-end' 
                                                    : 'bg-gradient-to-r from-orange-500 to-pink-500 text-white self-start'
                                            }`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <span className="font-semibold text-xs opacity-70">
                                                    {typingRole === "AI" ? "AI" : mode === "chat" ? "User" : "Caller"}
                                                </span>
                                                <span className="ml-2 text-xs opacity-70">
                                                    {mode === "chat" ? "typing…" : "speaking…"}
                                                </span>
                                            </div>
                                            <div className="mt-1">
                                                {typedText}
                                                <span className="inline-block w-2 h-4 ml-0.5 bg-white/70 animate-pulse" />
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Benefits */}
                <motion.div variants={containerVariants} className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                    {[
                        { icon: SparklesIcon, title: "Chat + Voice in One", description: "Same agent intelligence and knowledge base across your website widget and your phone number — designed once, deployed everywhere.", stats: "One platform" },
                        { icon: PlayIcon, title: "Live in Minutes", description: "Embed a script tag for chat, or attach a number for voice. Templates and vertical packs get you to a working agent fast.", stats: "< 5 min setup" },
                        { icon: ArrowRightIcon, title: "Unified CRM Inbox", description: "Chat, voice, email, SMS, WhatsApp, Instagram and Facebook all land in one ticket queue — identity resolved, AI transcript attached, ready for handover.", stats: "7 channels · 1 inbox" }
                    ].map((benefit, i) => (
                        <motion.div key={i} variants={cardVariants} className="group cursor-pointer h-full">
                            <div className="relative h-full p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:border-white/20 hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-1">
                                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-blue-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                <div className="relative z-10">
                                    <div className="h-16 w-16 flex items-center justify-center rounded-2xl bg-white/10 group-hover:bg-white/20 mb-6 transition-colors duration-300">
                                        <benefit.icon className="h-8 w-8 text-white" />
                                    </div>
                                    <div className="text-orange-500 text-sm font-semibold mb-2 opacity-80">{benefit.stats}</div>
                                    <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-500 transition-colors duration-300">{benefit.title}</h2>
                                    <p className="text-white/80 leading-relaxed">{benefit.description}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Stats */}
                <motion.div variants={containerVariants} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
                    {[
                        { number: "50K+", label: "Active Agents", subtext: "Chat + voice agents in production" },
                        { number: "16", label: "Languages", subtext: "Including Hindi & 9 Indic locales" },
                        { number: "24/7", label: "Coverage", subtext: "Inbound chat & calls round the clock" },
                        { number: "150ms", label: "Response Time", subtext: "Streaming for chat, sub-500ms for voice" }
                    ].map((stat, i) => (
                        <motion.div key={i} variants={statsVariants} className="relative group cursor-pointer">
                            <div className="text-center p-6 lg:p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:border-white/20 hover:bg-white/10 transition-all duration-300 h-full transform hover:-translate-y-1">
                                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                <div className="relative z-10">
                                    <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{stat.number}</div>
                                    <div className="text-lg font-semibold text-white mb-1">{stat.label}</div>
                                    <div className="text-sm text-white/60">{stat.subtext}</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>

            {/* Bottom gradient */}
            <div className="absolute inset-x-0 bottom-0 -z-10 transform-gpu overflow-hidden blur-3xl">
                <motion.div
                    animate={{
                        opacity: [0.15, 0.25, 0.15],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
                    className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-blue-500 via-emerald-500 to-indigo-600 opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
                />
            </div>
        </motion.div>
    );
}